import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import CanadianAddressFields from '../common/CanadianAddressFields';
import {
  fetchSellerTaxInformation,
  updateSellerTaxInformation
} from '../../services/sellerTaxService';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiInfo,
  FiLoader,
  FiLock,
  FiSave
} = FiIcons;

const PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Saskatchewan',
  'Yukon'
];

const EMPTY_PICKUP_ADDRESS = {
  line1: '',
  line2: '',
  city: '',
  province: 'Ontario',
  postal_code: '',
  country: 'CA'
};

const DEFAULTS = {
  tax_registration_status: 'unsure',
  gst_hst_registration_number: '',
  tax_business_province: 'Ontario',
  tax_acknowledgement_accepted: false,
  online_payment_requirements_complete: false,
  online_payments_eligible: false,
  pickup_tax_address: EMPTY_PICKUP_ADDRESS
};

const TaxInformationSection = ({ seller, expanded = false, onSaved }) => {
  const [formData, setFormData] = useState(DEFAULTS);
  const [isOpen, setIsOpen] = useState(expanded);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setIsOpen(expanded);
  }, [expanded]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!seller?.id) {
        setLoadingData(false);
        return;
      }

      try {
        const data = await fetchSellerTaxInformation(seller.id);

        if (active && data) {
          setFormData({
            ...DEFAULTS,
            ...data,
            pickup_tax_address: {
              ...EMPTY_PICKUP_ADDRESS,
              ...(data.pickup_tax_address || {})
            }
          });
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message || 'Unable to load tax information.');
        }
      } finally {
        if (active) setLoadingData(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [seller?.id]);

  const setField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const save = async (event) => {
    event.preventDefault();

    if (loading) return;

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const result = await updateSellerTaxInformation(seller.id, formData);

      setFormData((current) => ({
        ...current,
        online_payments_eligible: Boolean(result?.online_payments_eligible)
      }));
      setSuccess(true);
      setIsOpen(false);
      onSaved?.(result);
      window.setTimeout(() => setSuccess(false), 3000);
    } catch (saveError) {
      setError(saveError.message || 'Unable to save tax information.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center rounded-[2.5rem] border border-slate-100 bg-white p-12">
        <SafeIcon icon={FiLoader} className="animate-spin text-2xl text-indigo-600" />
      </div>
    );
  }

  const statusLabel = {
    registered: 'Registered',
    not_registered: 'Not registered',
    unsure: 'Unsure'
  }[formData.tax_registration_status];

  return (
    <section className="rounded-[2.5rem] border border-amber-100 bg-white p-8 shadow-sm md:p-10">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-start justify-between text-left"
        aria-expanded={isOpen}
      >
        <span className="flex items-start">
          <SafeIcon icon={FiLock} className="mr-3 mt-1 text-xl text-amber-600" />
          <span>
            <span className="block text-xl font-black text-slate-900">
              Tax Information
            </span>
            <span className="mt-1 block text-sm font-medium text-slate-500">
              Required only for platform-processed payments. This information is private and is never shown on your public seller profile.
            </span>
            {!isOpen && (
              <span className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest ${
                formData.online_payments_eligible
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700'
              }`}>
                <SafeIcon
                  icon={formData.online_payments_eligible ? FiCheckCircle : FiInfo}
                  className="mr-2"
                />
                {formData.online_payments_eligible
                  ? 'Complete'
                  : `${statusLabel} · Action required`}
              </span>
            )}
            {!isOpen && formData.pickup_tax_address.line1 && (
              <span className="mt-3 block rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-left">
                <span className="block text-[9px] font-black uppercase tracking-widest text-indigo-500">
                  Seller Hub pickup address
                </span>
                <span className="mt-1 block text-xs font-bold leading-relaxed text-slate-700">
                  {[
                    formData.pickup_tax_address.line1,
                    formData.pickup_tax_address.line2,
                    [
                      formData.pickup_tax_address.city,
                      formData.pickup_tax_address.province
                    ].filter(Boolean).join(', '),
                    formData.pickup_tax_address.postal_code
                  ].filter(Boolean).join(', ')}
                </span>
              </span>
            )}
          </span>
        </span>
        <SafeIcon
          icon={FiChevronDown}
          className={`mt-1 text-xl text-slate-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="mt-8">
          {error && (
            <div className="mb-6 flex items-center rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
              <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 flex items-center rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-700">
              <SafeIcon icon={FiCheckCircle} className="mr-3 text-lg" />
              Tax information and pickup address saved.
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="mb-3 block text-xs font-black uppercase tracking-widest text-slate-400">
                GST/HST registration
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  ['registered', 'Registered'],
                  ['not_registered', 'Not registered'],
                  ['unsure', 'Unsure']
                ].map(([value, label]) => (
                  <label
                    key={value}
                    className={`cursor-pointer rounded-2xl border p-4 text-sm font-black ${
                      formData.tax_registration_status === value
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-slate-100 bg-slate-50 text-slate-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tax_registration_status"
                      value={value}
                      checked={formData.tax_registration_status === value}
                      onChange={(event) => setField(
                        'tax_registration_status',
                        event.target.value
                      )}
                      className="mr-2"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {formData.tax_registration_status === 'registered' && (
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                  GST/HST registration number
                </label>
                <input
                  value={formData.gst_hst_registration_number}
                  onChange={(event) => setField(
                    'gst_hst_registration_number',
                    event.target.value
                  )}
                  inputMode="numeric"
                  autoComplete="off"
                  required
                  className="w-full rounded-2xl bg-slate-50 px-5 py-4 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="9-digit registration number"
                />
                <p className="mt-2 text-xs font-medium text-slate-400">
                  Stored securely and never displayed publicly.
                </p>
              </div>
            )}

            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                Business province or territory
              </label>
              <select
                value={formData.tax_business_province}
                onChange={(event) => setField(
                  'tax_business_province',
                  event.target.value
                )}
                className="w-full rounded-2xl bg-slate-50 px-5 py-4 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {PROVINCES.map((province) => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
              <p className="mt-2 text-xs font-medium text-slate-500">
                SOHLocal does not support sellers operating in Quebec.
              </p>
            </div>

            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-5">
              <h3 className="text-xs font-black uppercase tracking-widest text-indigo-900">
                Registered pickup address
              </h3>
              <p className="mb-4 mt-2 text-xs font-medium leading-relaxed text-indigo-700">
                Stripe Tax uses this private address for pickup orders instead of the buyer's billing address. A complete Canadian address is required to offer pickup checkout.
              </p>
              <CanadianAddressFields
                idPrefix="seller-pickup-address"
                value={formData.pickup_tax_address}
                onChange={(value) => setField('pickup_tax_address', value)}
                required={false}
              />
            </div>

            <label className="flex items-start rounded-2xl border border-slate-100 bg-slate-50 p-5">
              <input
                type="checkbox"
                checked={formData.tax_acknowledgement_accepted}
                onChange={(event) => setField(
                  'tax_acknowledgement_accepted',
                  event.target.checked
                )}
                className="mr-3 mt-1 h-5 w-5 rounded text-indigo-600"
              />
              <span className="text-sm font-bold leading-relaxed text-slate-700">
                I accept responsibility for my tax registration, collection, reporting and remittance obligations, except when SOHLocal is legally required to collect and remit tax.
              </span>
            </label>

            <label className="flex items-start rounded-2xl border border-slate-100 bg-slate-50 p-5">
              <input
                type="checkbox"
                checked={formData.online_payment_requirements_complete}
                onChange={(event) => setField(
                  'online_payment_requirements_complete',
                  event.target.checked
                )}
                className="mr-3 mt-1 h-5 w-5 rounded text-indigo-600"
              />
              <span className="text-sm font-bold leading-relaxed text-slate-700">
                I have completed all requirements needed to accept online payments.
              </span>
            </label>

            <div className={`flex items-start rounded-2xl p-5 ${
              formData.online_payments_eligible
                ? 'bg-emerald-50 text-emerald-800'
                : 'bg-amber-50 text-amber-800'
            }`}>
              <SafeIcon
                icon={formData.online_payments_eligible ? FiCheckCircle : FiInfo}
                className="mr-3 mt-1 text-lg"
              />
              <p className="text-sm font-bold">
                {formData.online_payments_eligible
                  ? 'Your tax information permits online-payment activation, subject to Stripe verification.'
                  : 'Online payments remain disabled until every required tax field is complete.'}
              </p>
            </div>

            <button
              type="button"
              onClick={save}
              disabled={loading}
              className="flex w-full items-center justify-center rounded-2xl bg-indigo-600 px-6 py-4 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
            >
              <SafeIcon
                icon={loading ? FiLoader : FiSave}
                className={`mr-2 ${loading ? 'animate-spin' : ''}`}
              />
              {loading ? 'Saving...' : 'Save Tax Information'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default TaxInformationSection;
